import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatCard } from "@/components/StatCard";
import { LeadFormDialog } from "@/components/LeadFormDialog";
import { CsvImportDialog } from "@/components/CsvImportDialog";
import { BUCKET_STYLES, URGENCY_STYLES, fmtDate } from "@/lib/constants";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Plus, Upload, X, Globe } from "lucide-react";

const FILTERS = [
  { key: "bucket", label: "Bucket", from: "buckets" },
  { key: "deal_stage", label: "Deal Stage", from: "deal_stages" },
  { key: "recommended_offer", label: "Offer", from: "offers_keys" },
  { key: "urgency", label: "Urgency", static: ["High", "Medium", "Low"] },
  { key: "lead_source", label: "Source", from: "lead_sources" },
];

export default function Dashboard({ role }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = role === "admin";
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [config, setConfig] = useState(null);
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const loadLeads = useCallback(async () => {
    const params = { ...filters };
    if (search) params.search = search;
    const res = await api.get("/leads", { params });
    setLeads(res.data);
  }, [filters, search]);

  const loadStats = useCallback(async () => {
    const res = await api.get("/stats");
    setStats(res.data);
  }, []);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadLeads(), loadStats()]).finally(() => setLoading(false));
  }, [loadLeads, loadStats]);

  const setFilter = (key, val) => {
    setFilters((f) => {
      const next = { ...f };
      if (!val || val === "__all__") delete next[key];
      else next[key] = val;
      return next;
    });
  };

  const optionsFor = (f) => {
    if (f.static) return f.static;
    if (!config) return [];
    if (f.from === "offers_keys") return Object.keys(config.offers || {});
    return config[f.from] || [];
  };

  const statCards = [
    { label: "Total Leads", value: stats.total, tone: "default", key: null, testid: "stat-total" },
    { label: "Hot Opportunities", value: stats.hot_opportunity, tone: "hot", key: "Hot Opportunity", testid: "stat-hot" },
    { label: "Qualified Leads", value: stats.qualified_lead, tone: "qualified", key: "Qualified Lead", testid: "stat-qualified" },
    { label: "Education Leads", value: stats.education_lead, tone: "education", key: "Education Lead", testid: "stat-education" },
    { label: "Low Fit", value: stats.low_fit, tone: "low", key: "Low Fit", testid: "stat-lowfit" },
    { label: "Proposal Ready", value: stats.proposal_ready, tone: "ready", key: "Proposal Ready", testid: "stat-proposal-ready" },
    { label: "Proposal Sent", value: stats.proposal_sent, tone: "sent", key: "Proposal Sent", testid: "stat-proposal-sent" },
    { label: "Closed Won", value: stats.closed_won, tone: "won", key: null, testid: "stat-won" },
    { label: "Closed Lost", value: stats.closed_lost, tone: "lost", key: null, testid: "stat-lost" },
  ];

  const activeFilterCount = Object.keys(filters).length;

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6 sticky top-0 z-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="overline text-gold mb-1.5">CMMC Readiness Pipeline</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight leading-none">
              Sales Command Center
            </h1>
          </div>
          <div className="flex gap-2">
            <Button data-testid="import-csv-button" variant="outline" onClick={() => setShowImport(true)}
              className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white">
              <Upload className="h-4 w-4 mr-2" /> Import CSV
            </Button>
            <Button data-testid="add-lead-button" onClick={() => setShowForm(true)}
              className="bg-gold text-navy hover:bg-gold-dark font-semibold">
              <Plus className="h-4 w-4 mr-2" /> Add Lead
            </Button>
          </div>
        </div>
      </header>

      <div className="p-6 md:p-8 space-y-6">
        {/* Quick widgets */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="dashboard-widgets">
          {[
            { label: "New Quiz Leads", value: stats.new_quiz_leads, testid: "widget-new-quiz" },
            { label: "New SAM Leads", value: stats.new_sam_leads, testid: "widget-new-sam" },
            { label: "Hot Opportunities", value: stats.hot_opportunity, testid: "widget-hot" },
            { label: "Proposal Sent", value: stats.proposal_sent, testid: "widget-proposal-sent" },
            { label: "Follow-ups Due", value: stats.followups_due, testid: "widget-followups" },
          ].map((w) => (
            <div key={w.label} data-testid={w.testid} className="rounded-md border border-border bg-navy text-white p-4">
              <div className="overline text-gold mb-2">{w.label}</div>
              <div className="font-display text-3xl font-bold leading-none">{w.value ?? 0}</div>
            </div>
          ))}
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {statCards.map((c) => (
            <StatCard
              key={c.label}
              testid={c.testid}
              label={c.label}
              value={c.value}
              tone={c.tone}
              active={c.key && filters.bucket === c.key}
              onClick={() => c.key !== null && setFilter("bucket", filters.bucket === c.key ? null : c.key)}
            />
          ))}
        </div>

        {/* Filters */}
        <div className="rounded-md border border-border bg-card p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="search-input"
                placeholder="Search company or contact…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {FILTERS.map((f) => (
              <Select key={f.key} value={filters[f.key] || "__all__"} onValueChange={(v) => setFilter(f.key, v)}>
                <SelectTrigger data-testid={`filter-${f.key}`} className="w-[170px]">
                  <SelectValue placeholder={f.label} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All {f.label}</SelectItem>
                  {optionsFor(f).map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ))}
            {activeFilterCount > 0 && (
              <Button data-testid="clear-filters" variant="ghost" size="sm" onClick={() => setFilters({})}
                className="text-muted-foreground">
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto table-scroll">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-border text-left">
                  {["Company", "Contact", "Email", "Phone", "Website", "CAGE", "Source",
                    ...(isAdmin ? ["Score"] : []), "Bucket", "Offer", "Urgency", "Deal Stage", "Next Action", "Updated"].map((h) => (
                    <th key={h} className="px-4 py-3 font-semibold text-navy whitespace-nowrap text-xs uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={20} className="px-4 py-12 text-center text-muted-foreground">Loading leads…</td></tr>
                ) : leads.length === 0 ? (
                  <tr><td colSpan={20} className="px-4 py-12 text-center text-muted-foreground" data-testid="empty-state">No leads match your filters.</td></tr>
                ) : (
                  leads.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`lead-row-${l.id}`}
                      onClick={() => navigate(`/leads/${l.id}`)}
                      className="border-b border-border last:border-0 hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-navy whitespace-nowrap">{l.company_name}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{l.contact_name || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{l.email || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{l.phone || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {l.website ? <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" />{l.website}</span> : "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">{l.cage_code || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{l.lead_source}</td>
                      {isAdmin && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-mono font-semibold text-navy">{l.score ?? "—"}</span>
                        </td>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${BUCKET_STYLES[l.bucket] || "bg-slate-100"}`}>
                          {l.bucket}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs max-w-[160px] truncate" title={l.recommended_offer}>{l.recommended_offer}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${URGENCY_STYLES[l.urgency] || ""}`}>
                          {l.urgency}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs">{l.deal_stage}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground max-w-[200px] truncate" title={l.next_action}>{l.next_action}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">{fmtDate(l.updated_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {config && (
        <>
          <LeadFormDialog open={showForm} onOpenChange={setShowForm} config={config}
            onSaved={() => { loadLeads(); loadStats(); }} />
          <CsvImportDialog open={showImport} onOpenChange={setShowImport}
            onImported={() => { loadLeads(); loadStats(); }} />
        </>
      )}
    </div>
  );
}
